'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';

const profileAnimation = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.7, ease: 'easeOut' as const },
};

export default function CaseStudySection() {
  const [showLeonardoImage, setShowLeonardoImage] = useState(true);

  return (
    <section id="specialist" className="w-full bg-[#02020c] py-[28px] sm:py-[42px] lg:py-[56px] px-[14px] sm:px-[21px] lg:px-[28px]">
      <div className="w-full max-w-7xl mx-auto">
        <div className="bg-[#ffffff28] border border-[#ffffff28] rounded-[32px] sm:rounded-[48px] lg:rounded-[64px] py-[28px] sm:py-[42px] lg:py-[56px] px-[20px] sm:px-[64px] lg:px-[86px]">
          <motion.div
            initial={profileAnimation.initial}
            whileInView={profileAnimation.whileInView}
            viewport={profileAnimation.viewport}
            transition={profileAnimation.transition}
            className="flex flex-col-reverse lg:flex-row items-start justify-between gap-[20px] sm:gap-[30px] lg:gap-[40px] w-full"
          >
            {/* Case Study Content */}
            <div className="flex flex-col justify-start items-start w-full lg:w-[50%]">
              {/* Badge */}
              <div className="bg-[#ffffff] border border-gradient-to-r from-[#0c152600] via-[#14233f] to-[#0c152600] rounded-[18px] py-[4px] sm:py-[6px] lg:py-[8px] px-[11px] sm:px-[16px] lg:px-[22px]">
                <span 
                  className="text-[8px] sm:text-[12px] lg:text-[16px] font-normal leading-[10px] sm:leading-[14px] lg:leading-[19px] text-center text-[#0d2f3b]"
                  style={{ fontFamily: 'Raleway' }}
                >
                  Quem Está Por Trás Dessa Solução?
                </span>
              </div>

              {/* Client Name */}
              <h3 
                className="text-[24px] sm:text-[36px] lg:text-[48px] font-semibold leading-[29px] sm:leading-[43px] lg:leading-[57px] text-left text-[#ffffff] mt-[7px] sm:mt-[10px] lg:mt-[14px]"
                style={{ fontFamily: 'Raleway' }}
              >
                Fabrício Silva
              </h3>

              {/* Case Description */}
              <p 
                className="text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[12px] sm:leading-[17px] lg:leading-[23px] text-left text-[#ffffff] w-full mt-[7px] sm:mt-[10px] lg:mt-[14px]"
                style={{ fontFamily: 'Raleway' }}
              >
                <span className="font-bold italic text-[13px] sm:text-[17px] lg:text-[22px]">&quot;Eu Sei Por Que Sua IA Não Funcionou — E Sei Como Consertar Isso&quot;</span>
                <br /><br />
                Deixa eu adivinhar: você já tentou implementar IA no comercial e foi uma frustração atrás da outra.
                <br/><br/>
                A IA não seguia o script de qualificação direito. Os follow-ups eram enviados em horários absurdos (ou simplesmente não eram enviados). Você até perdeu o número do WhatsApp porque usaram uma API não oficial.
                <br/><br/>
                <span className="font-bold">Eu vejo isso acontecer toda semana.</span>
                <br/><br/>
                Meu nome é <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Fabrício Silva</span>, sou CEO da Evolutta, e meu trabalho é consertar IAs que outros quebraram — ou construir do zero da forma certa desde o início.
                <br/><br/>
                <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Por que eu consigo fazer o que outros não conseguem?</span>
                <br/><br/>
                Tenho uma formação que 99% do mercado não tem: <span className="font-bold italic text-[11px] sm:text-[15px] lg:text-[20px]">sou Engenheiro de Petróleo e Produção (então entendo processos complexos de verdade) e Desenvolvedor Full Stack (então construo as soluções na mão, do zero, sem depender de ferramentas que limitam).</span>
                <br/><br/>
                Essa combinação me permite criar agentes de IA que:
              </p>
              <ul className="list-disc pl-[15px] sm:pl-[20px] lg:pl-[25px] mt-[5px] text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[14px] sm:leading-[20px] lg:leading-[26px] text-left text-[#ffffff] space-y-[4px]" style={{ fontFamily: 'Raleway' }}>
                <li>Seguem a lógica do SEU processo comercial</li>
                <li>Usam apenas APIs oficiais e seguras</li>
                <li>Fazem follow-up no timing certo, com a mensagem certa</li>
                <li>Vêm com dashboard customizado para você ver tudo funcionando</li>
              </ul>
              <br/><br/>
              <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Minha experiência não é teórica</span>
              <p className="text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[12px] sm:leading-[17px] lg:leading-[23px] text-left text-[#ffffff] w-full mt-[7px] sm:mt-[10px] lg:mt-[14px]"
                style={{ fontFamily: 'Raleway' }}>
              Já construí 20+ agentes de SDR e Follow-Up que estão operando agora. Trabalho diariamente com agências ajudando em dezenas de projetos simultâneos. Sou Professor & Gestor Sênior na DinastIA, a maior comunidade de IA do Brasil.
              <br/><br/>
              <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Aqui está minha promessa para você:</span>
              <br/><br/>
              Não sou mais um fornecedor. 
              <br/><br/>
              Sou um parceiro de negócios especialista em IA.
              <br/><br/>
              Vou entender seu negócio, analisar sua estrutura e implementar IA com profissionalismo e segurança. 
              <br/><br/>
              Não para seu negócio &quot;ter uma IA&quot;, mas para aumentar seu faturamento e ROI de forma mensurável.
              <br/><br/>
              Se você já se queimou com soluções genéricas e está procurando alguém que realmente sabe construir IA que funciona, vamos conversar.
              <br/><br/>
              Serei a última empresa de IA que você precisará no seu negócio.

              </p>

              {/* Divider */}
              <div className="w-full h-[1px] bg-[#ffffff28] mt-[10px] sm:mt-[15px] lg:mt-[20px]"></div>

              {/* Result Stats */}
              <div className="flex items-center justify-start gap-[5px] sm:gap-[7px] lg:gap-[10px] w-full mt-[14px] sm:mt-[21px] lg:mt-[28px]">
                <img
                  src="/images/img_group_79.svg"
                  alt="Growth Icon"
                  className="w-[11px] h-[11px] sm:w-[16px] sm:h-[16px] lg:w-[22px] lg:h-[22px]"
                />
                <span 
                  className="text-[10px] sm:text-[15px] lg:text-[20px] font-normal leading-[12px] sm:leading-[18px] lg:leading-[24px] text-left text-[#ffffff] ml-[5px] sm:ml-[7px] lg:ml-[10px]"
                  style={{ fontFamily: 'Raleway' }}
                >
                  CEO da Evolutta | Engenheiro de Petróleo e Produção | Desenvolvedor Full Stack | Especialista em IA
                </span>
              </div>
            </div>

            {/* Case Study Visual */}
            <div className="relative w-full lg:w-[40%] lg:sticky lg:top-[56px]">
              <div className="bg-gradient-to-r from-[#cef3ff] via-[#00c3ff] to-[#3c3cce] border border-[#ffffff14] rounded-[36px] w-full h-[243px] sm:h-[364px] lg:h-[486px]">
                {/* Main Image */}
                <img
                  src="./images/img_imagem_do_whatsapp.jpeg"
                  alt="Case Study Visual"
                  className="w-full h-full object-cover rounded-[36px]"
                  style={{ objectPosition: 'center 20%' }}
                />

                {/* Overlay Content */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#02020c] rounded-[36px] p-[9px] sm:p-[13px] lg:p-[18px] flex flex-col justify-between">
                  {/* Play Button */}
                  <div className="flex justify-end">
                    <img
                      src="./images/img_group_281.svg"
                      alt="Play Button"
                      className="w-[21px] h-[21px] sm:w-[31px] sm:h-[31px] lg:w-[42px] lg:h-[42px]"
                    />
                  </div>

                  {/* Bottom Content */}
                  <div className="flex flex-col gap-[1px] sm:gap-[1px] lg:gap-[2px] justify-start items-start w-full px-[17px] sm:px-[25px] lg:px-[34px] pb-[9px] sm:pb-[13px] lg:pb-[18px]">
                    <span 
                      className="text-[15px] sm:text-[22px] lg:text-[30px] font-normal leading-[19px] sm:leading-[28px] lg:leading-[37px] text-left bg-gradient-to-b from-[#f7faff] to-[#9eb5dc] bg-clip-text text-transparent"
                      style={{ fontFamily: 'Raleway' }}
                    >
                      Fabrício Silva
                    </span>
                    <span 
                      className="text-[8px] sm:text-[11px] lg:text-[15px] font-normal leading-[10px] sm:leading-[14px] lg:leading-[19px] text-left bg-gradient-to-r from-[#cef3ff] to-[#7c9299] bg-clip-text text-transparent"
                      style={{ fontFamily: 'Raleway' }}
                    >
                      Especialista em IA
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={profileAnimation.initial}
            whileInView={profileAnimation.whileInView}
            viewport={profileAnimation.viewport}
            transition={{ ...profileAnimation.transition, delay: 0.1 }}
            className="flex flex-col lg:flex-row items-start justify-between gap-[20px] sm:gap-[30px] lg:gap-[40px] w-full mt-[28px] sm:mt-[42px] lg:mt-[56px] pt-[28px] sm:pt-[42px] lg:pt-[56px] border-t border-[#ffffff1f]"
          >
            <div className="relative w-full lg:w-[40%] lg:sticky lg:top-[56px]">
              <div className="bg-gradient-to-r from-[#cef3ff] via-[#00c3ff] to-[#3c3cce] border border-[#ffffff14] rounded-[36px] w-full h-[243px] sm:h-[364px] lg:h-[486px] p-[1px]">
                <div className="relative w-full h-full rounded-[35px] overflow-hidden bg-[#02020c]">
                  {showLeonardoImage ? (
                    <img
                      src="/images/leonardo-freire.png"
                      alt="Leonardo Freire"
                      className="w-full h-full object-cover rounded-[35px]"
                      style={{ objectPosition: '50% 15%' }}
                      onError={() => setShowLeonardoImage(false)}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(9,20,35,0.82)_0%,rgba(2,2,12,0.96)_100%)]">
                      <div className="absolute inset-[18px] sm:inset-[24px] lg:inset-[30px] rounded-[30px] border border-dashed border-[#d7e8ff33] bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.10),transparent_55%),linear-gradient(180deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0.02)_100%)]" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-[12px] px-[18px] text-center">
                        <div className="flex h-[82px] w-[82px] sm:h-[104px] sm:w-[104px] lg:h-[128px] lg:w-[128px] items-center justify-center rounded-full border border-[#ffffff1f] bg-[#0b111dcc] shadow-[0_18px_48px_rgba(0,0,0,0.28)]">
                          <span
                            className="text-[26px] sm:text-[34px] lg:text-[42px] font-semibold leading-none text-[#f7faff]"
                            style={{ fontFamily: 'Raleway' }}
                          >
                            LF
                          </span>
                        </div>
                        <span
                          className="max-w-[260px] text-[11px] sm:text-[14px] lg:text-[17px] font-normal leading-[16px] sm:leading-[20px] lg:leading-[24px] text-[#dbe7f6]"
                          style={{ fontFamily: 'Raleway' }}
                        >
                          Adicione o arquivo /images/leonardo-freire.png para aplicar automaticamente o recorte com foco no rosto.
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#02020c]" />
                  <div className="absolute inset-0 z-10 flex h-full flex-col justify-end p-[18px] sm:p-[24px] lg:p-[30px]">
                    <div className="flex flex-col gap-[2px] justify-start items-start w-full px-[17px] sm:px-[25px] lg:px-[34px] pb-[9px] sm:pb-[13px] lg:pb-[18px]">
                      <span
                        className="text-[15px] sm:text-[22px] lg:text-[30px] font-normal leading-[19px] sm:leading-[28px] lg:leading-[37px] text-left bg-gradient-to-b from-[#f7faff] to-[#9eb5dc] bg-clip-text text-transparent"
                        style={{ fontFamily: 'Raleway' }}
                      >
                        Leonardo Freire
                      </span>
                      <span
                        className="text-[8px] sm:text-[11px] lg:text-[15px] font-normal leading-[10px] sm:leading-[14px] lg:leading-[19px] text-left bg-gradient-to-r from-[#cef3ff] to-[#7c9299] bg-clip-text text-transparent"
                        style={{ fontFamily: 'Raleway' }}
                      >
                        Leonardo Freire | Fundador e CEO da IaSolution & Especialista em Engenharia de IA
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-start items-start w-full lg:w-[50%]">
              <div className="bg-[#ffffff] border border-gradient-to-r from-[#0c152600] via-[#14233f] to-[#0c152600] rounded-[18px] py-[4px] sm:py-[6px] lg:py-[8px] px-[11px] sm:px-[16px] lg:px-[22px]">
                <span
                  className="text-[8px] sm:text-[12px] lg:text-[16px] font-normal leading-[10px] sm:leading-[14px] lg:leading-[19px] text-center text-[#0d2f3b]"
                  style={{ fontFamily: 'Raleway' }}
                >
                  IaSolution
                </span>
              </div>

              <h3
                className="text-[24px] sm:text-[36px] lg:text-[48px] font-semibold leading-[29px] sm:leading-[43px] lg:leading-[57px] text-left text-[#ffffff] mt-[7px] sm:mt-[10px] lg:mt-[14px]"
                style={{ fontFamily: 'Raleway' }}
              >
                Leonardo Freire
              </h3>

              <p
                className="text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[12px] sm:leading-[17px] lg:leading-[23px] text-left text-[#ffffff] w-full mt-[7px] sm:mt-[10px] lg:mt-[14px]"
                style={{ fontFamily: 'Raleway' }}
              >
                <span className="font-bold italic text-[13px] sm:text-[17px] lg:text-[22px]">
                  &quot;Engenharia de IA Não é Brincadeira — E Nós Construímos para Escalar&quot;
                </span>
                <br /><br />
                Você já deve ter percebido: uma IA é tão boa quanto a infraestrutura que a sustenta. Não adianta ter um prompt bonito se o sistema cai, o banco de dados trava ou a integração com seu CRM é uma gambiarra.
                <br /><br />
                Enquanto o mercado entrega soluções que quebram na primeira escala, meu foco é a engenharia de alta disponibilidade e performance.
                <br /><br />
                Meu nome é <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Leonardo Freire</span>, sou Fundador e CEO da IaSolution, e meu trabalho é garantir que a tecnologia por trás do seu agente de IA seja inabalável.
                <br /><br />
                <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Por que nossa entrega é superior?</span>
                <br /><br />
                Sou Desenvolvedor Sênior e Especialista em Arquitetura de Software. Enquanto outros usam ferramentas no-code limitadas, eu construo o core da solução.
              </p>

              <ul className="list-disc pl-[15px] sm:pl-[20px] lg:pl-[25px] mt-[5px] text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[14px] sm:leading-[20px] lg:leading-[26px] text-left text-[#ffffff] space-y-[4px]" style={{ fontFamily: 'Raleway' }}>
                <li>Suportam milhares de requisições simultâneas sem perder performance</li>
                <li>Integram-se nativamente a qualquer ERP ou CRM do mercado via API oficial</li>
                <li>Possuem camadas de segurança e criptografia de nível enterprise</li>
                <li>Operam com lógica de Vibe Coding para evolução rápida e constante</li>
              </ul>

              <p
                className="text-[9px] sm:text-[13px] lg:text-[18px] font-medium leading-[12px] sm:leading-[17px] lg:leading-[23px] text-left text-[#ffffff] w-full mt-[14px] sm:mt-[18px] lg:mt-[24px]"
                style={{ fontFamily: 'Raleway' }}
              >
                <span className="font-bold text-[11px] sm:text-[15px] lg:text-[20px]">Minha autoridade no campo:</span>
                <br /><br />
                Sou Professor Sênior na DinastIA, onde ensino centenas de desenvolvedores a criarem o futuro. Minha rotina é transformar ideias complexas em código de alta performance que gera lucro real e previsível.
                <br /><br />
                Minha promessa é clara: entregamos o que há de mais avançado em engenharia de IA no Brasil. Sem desculpas, apenas código que funciona.
              </p>

              <div className="w-full h-[1px] bg-[#ffffff28] mt-[10px] sm:mt-[15px] lg:mt-[20px]"></div>

              <div className="flex items-center justify-start gap-[5px] sm:gap-[7px] lg:gap-[10px] w-full mt-[14px] sm:mt-[21px] lg:mt-[28px]">
                <img
                  src="/images/img_group_79.svg"
                  alt="Growth Icon"
                  className="w-[11px] h-[11px] sm:w-[16px] sm:h-[16px] lg:w-[22px] lg:h-[22px]"
                />
                <span
                  className="text-[10px] sm:text-[15px] lg:text-[20px] font-normal leading-[12px] sm:leading-[18px] lg:leading-[24px] text-left text-[#ffffff] ml-[5px] sm:ml-[7px] lg:ml-[10px]"
                  style={{ fontFamily: 'Raleway' }}
                >
                  Leonardo Freire | Fundador e CEO da IaSolution & Especialista em Engenharia de IA
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
